import React, { FC } from 'react';
import styled from 'styled-components';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import analytics from '@ad-scripts/lib/analytics';
import endpointsResource from 'src/actions/endpoints/endpointsResource';
import merge from 'lodash/merge';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { chatConfigurationDefaults } from '../model/chatModel';
import { useDispatch } from 'react-redux';
import { validateAddress } from '../helpers/validateAddress';
import { validateAddressUnique } from '../helpers/validateAddressUnique';

import { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import useRouter from 'src/presentation/hooks/useRouter';
import { Organization } from 'src/actions/organization/organizationResource';
import cloneDeep from 'lodash/cloneDeep';
import InputWithPrefix from 'src/presentation/components/forms/InputWithPrefix';
import { ModalContainer } from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/shared/elements';
import { CHAT_BRAND } from 'src/constants/branding';

const { validate } = endpointsResource.effects;
const { resetErrors } = endpointsResource.actions;

const DESCRIPTION = (
  <span>
    Enter a name to identify {CHAT_BRAND.name}. The name will be created as the {CHAT_BRAND.name} ID, appear in the
    embed code, and cannot be
    <br /> changed once created.
  </span>
);

function useOnSave(organization: Organization, closeModal?: Function) {
  const { history } = useRouter();
  const dispatch = useDispatch();

  return ({ workingState }) => {
    dispatch(resetErrors({}));
    const config = cloneDeep(workingState);
    config.address = `${organization.loginDomain}-${config.address.trim()}`;
    if (!dispatch(validateAddressUnique(config)) || !dispatch(validateAddress(config))) {
      return false;
    }

    closeModal && closeModal();
    history.push(`/chat/new?name=${config.address}`);
  };
}

const EditForm = ({ formContext, organization }) => (
  <FormContainer>
    <Form context={formContext}>
      <InputChatNameContainer>
        <InputWithPrefix
          autoFocus={true}
          attr="address"
          prefix={`${organization.loginDomain}-`}
          formContext={formContext}
          placeholder={''}
        />
      </InputChatNameContainer>
    </Form>
  </FormContainer>
);

const InputChatNameContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  max-width: 400px;

  [data-aid='address-error'] {
    position: absolute;
    top: 50px;
    width: 450px;
  }
`;

type AddNewChatModalProps = {
  onCancel?: Function;
  errors?: any;
  organization: Organization;
};

export const AddNewChatModal: FC<AddNewChatModalProps> = ({ errors, onCancel, organization }) => {
  const initializedItem = merge({}, chatConfigurationDefaults, { address: '', type: 'CHAT', version: 1 });
  const saveHandler = useOnSave(organization, onCancel);
  const dispatch = useDispatch();

  const handleCancel = () => {
    analytics.track('Admin - Add new Sidekick');
    dispatch(resetErrors({}));
    onCancel && onCancel();
  };
  const formContext = useForm({
    errors,
    item: initializedItem,
    onSave: saveHandler,
    resetErrors,
    validate,
  });

  const { onSave } = formContext;

  return (
    <ModalContainer
      title={`Create ${CHAT_BRAND.name}`}
      modalSize={ModalSize.LARGE}
      content={EmbedModalContent({ context: formContext, onCancel: handleCancel, onSave, organization })}
      onCancel={handleCancel}
      disableOutsideClick={true}
    />
  );
};

const EmbedModalContent = ({ context, onCancel, onSave, organization }) => {
  const { workingState, errors } = context;
  const hasErrors = errors && errors.length > 0;
  const address = workingState.address;
  return (
    <StyledModal>
      <StyledContent hasErrors={hasErrors}>
        <StyledHeader>{CHAT_BRAND.name} ID</StyledHeader>
        <DescriptionContainer>{DESCRIPTION}</DescriptionContainer>
        <EditForm formContext={context} organization={organization} />
      </StyledContent>
      <StyledFooter>
        <Button data-aid={'editor-cancel'} type={Button.Types.TEXT} onClick={onCancel}>
          Cancel
        </Button>
        <Button data-aid={'editor-save'} disabled={!address} onClick={onSave}>
          Create
        </Button>
      </StyledFooter>
    </StyledModal>
  );
};

const StyledModal = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px;
`;

const StyledHeader = styled.div`
  font-size: 13px;
  font-weight: 700;
`;

const StyledContent = styled.div`
  padding-top: 0;
  padding-bottom: 24px;
  padding-right: 24px;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  ${({ hasErrors }) => hasErrors && `padding-bottom: 40px;`}
`;

const FormContainer = styled.div`
  text-align: left;
`;

const StyledFooter = styled(Footer)`
  border-top: 1px solid ${({ theme }) => theme.colors.gray300};
  justify-content: end;
  padding-top: 24px;
  padding-left: 24px;
  padding-bottom: 0px;
  padding-right: 24px;
`;

const DescriptionContainer = styled.div`
  color: ${p => p.theme.colors.gray700};
  line-height: 162%;
  text-align: left;
`;
